<template>
  <div class="vote-works">
    <div v-if="tabsText.length" class="tabs">
      <span
        v-for="(item, index) in tabsText"
        :key="index"
        :class="{ videoActive: tabsActive === index }"
        @click="handleTabsActive(index)"
      >
        <img
          :src="tabsActive === index ? item.iconBgActive : item.iconBg"
          class="tabs-img"
        >
        {{ item.label }}
      </span>
    </div>

    <div class="vote-box-works">
      <div v-for="item in list" :key="item.id" class="works-box">
        <div v-if="item.voteWorksVo" class="works-title complete-bg">
          <div class="works-info">
            <span class="works-date">{{ formatDate(item.createTime) }}</span>
            <span class="works-number">
              <span v-text="labels.worksNumber"></span>
              {{ item.voteWorksVo.worksNumber || '--' }}
            </span>
          </div>
          <div class="works-btn">
            <span v-if="canEdit(item.voteWorksVo)" @click="handleEdit(item.voteWorksVo)" v-text="labels.edit"></span>
            <span @click="handleView(item.voteWorksVo.id)" v-text="labels.view"></span>
            <span v-if="canCancel(item.voteWorksVo)" @click="handleCancel(item)" v-text="labels.cancel"></span>
          </div>
        </div>

        <div v-if="item.voteWorksVo" class="works-content complete-border">
          <div class="con-primary">
            <img :src="getCover(item.voteWorksVo)" class="vote-con-img" @error="handleImageError">

            <div class="con-info">
              <h5>{{ item.voteWorksVo.name || '--' }}</h5>
              <div class="meta-row">
                <span class="meta-label" v-text="labels.author"></span>
                <span class="meta-value">{{ getAuthorName(item.voteWorksVo) }}</span>
              </div>
              <div v-if="getCreatorTypeLabel(item.voteWorksVo)" class="meta-row">
                <span class="meta-label" v-text="labels.creatorType"></span>
                <span class="meta-value">{{ getCreatorTypeLabel(item.voteWorksVo) }}</span>
              </div>
            </div>
          </div>

          <div class="bdl1 con-school">
            <h5>{{ getOrganizationTitle(item.voteWorksVo) }}</h5>
            <template v-if="isProfessionalParticipant(item.voteWorksVo)">
              <div v-if="getCompanyName(item.voteWorksVo)" class="meta-row">
                <span class="meta-label" v-text="labels.company"></span>
                <span class="meta-value">{{ getCompanyName(item.voteWorksVo) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label" v-text="labels.industryField"></span>
                <span class="meta-value">{{ getIndustryField(item.voteWorksVo) || '--' }}</span>
              </div>
            </template>
            <div v-else-if="getMentorText(item.voteWorksVo)" class="meta-row mentor-line">
              <span class="meta-label" v-text="labels.mentor"></span>
              <span class="meta-value mentor-text">{{ getMentorText(item.voteWorksVo) }}</span>
            </div>
            <div v-if="getWorkSizeText(item.voteWorksVo)" class="meta-row">
              <span class="meta-label" v-text="labels.workSize"></span>
              <span class="meta-value">{{ getWorkSizeText(item.voteWorksVo) }}</span>
            </div>
            <div v-if="item.voteWorksVo.weight" class="meta-row">
              <span class="meta-label" v-text="labels.weight"></span>
              <span class="meta-value">{{ item.voteWorksVo.weight }}</span>
            </div>
          </div>

          <div class="bdl1 con-plate">
            <h5>{{ item.voteWorksVo.plateName || '--' }}</h5>
            <div class="meta-row">
              <span class="meta-label" v-text="labels.category"></span>
              <span class="meta-value">{{ item.voteWorksVo.categoryName || '--' }}</span>
            </div>
          </div>

          <div class="bdl1 con-activity">
            <span class="activity-name">{{ item.voteWorksVo.activityName || '--' }}</span>
            <div v-if="item.voteWorksVo.workCount" class="activity-meta">
              <span class="meta-label" v-text="labels.workCount"></span>
              <span class="meta-value">{{ item.voteWorksVo.workCount }}</span>
            </div>
            <div v-if="item.voteWorksVo.signupEndTime" class="activity-meta">
              <span class="meta-label" v-text="labels.signupEnd"></span>
              <span class="meta-value">{{ formatDate(item.voteWorksVo.signupEndTime) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!list.length" class="empty-state" v-text="labels.empty"></div>
    </div>

    <div v-if="total > 0" class="art-pagination">
      <a-pagination
        size="small"
        :total="total"
        :current="params.page"
        :page-size="params.limit"
        @change="onChange"
      />
    </div>
  </div>
</template>

<script>
import { userVoteWorks, userCancelVoteWorks } from "@/api/UserCenter";
import { replaceBrokenImage, resolveWorkCover } from "@/utils/workImage";
import { normalizeVoteMeta } from "@/utils/voteMeta";

const FALLBACK_IMAGE = require("@/assets/images/nopic.png");
const LABELS = {
  tabTitle: "\u53c2\u8d5b\u4f5c\u54c1",
  worksNumber: "\u4f5c\u54c1\u7f16\u53f7\uff1a",
  edit: "\u4fee\u6539\u4f5c\u54c1",
  view: "\u67e5\u770b\u4f5c\u54c1",
  cancel: "\u53d6\u6d88\u53c2\u8d5b",
  author: "\u4f5c\u8005\uff1a",
  creatorType: "\u53c2\u8d5b\u5f62\u5f0f\uff1a",
  mentor: "\u5bfc\u5e08\uff1a",
  company: "\u5f53\u524d\u5355\u4f4d\uff1a",
  industryField: "\u4ece\u4e1a\u65b9\u5411\uff1a",
  workSize: "\u5c3a\u5bf8\uff1a",
  weight: "\u91cd\u91cf\uff1a",
  category: "\u4f5c\u54c1\u7c7b\u522b\uff1a",
  workCount: "\u4f5c\u54c1\u6570\u91cf\uff1a",
  signupEnd: "\u62a5\u540d\u622a\u6b62\uff1a",
  empty: "\u6682\u65e0\u53c2\u8d5b\u4f5c\u54c1\u3002",
  defaultWork: "\u8be5\u4f5c\u54c1",
  cancelTitle: "\u786e\u8ba4\u53d6\u6d88\u53c2\u8d5b\uff1f",
  cancelOk: "\u786e\u8ba4\u53d6\u6d88",
  cancelBack: "\u518d\u60f3\u60f3",
  cancelSuccess: "\u5df2\u53d6\u6d88\u53c2\u8d5b",
  cancelContentPrefix: "\u53d6\u6d88\u540e\u5c06\u5220\u9664\u201c",
  cancelContentSuffix: "\u201d\u5f53\u524d\u7684\u53c2\u8d5b\u8bb0\u5f55\uff0c\u662f\u5426\u7ee7\u7eed\uff1f",
  team: "\u56e2\u961f",
  personal: "\u4e2a\u4eba",
};

export default {
  name: "VoteWorks",
  data() {
    return {
      labels: LABELS,
      tabsActive: 0,
      tabsText: [
        {
          label: LABELS.tabTitle,
          iconBg: require("@/assets/img/user/video.png"),
          iconBgActive: require("@/assets/img/user/video-active.png"),
        },
      ],
      listData: [],
      params: {
        limit: 8,
        page: 1,
      },
      total: 0,
      totalPage: 0,
    };
  },
  computed: {
    currentActivityId() {
      const raw = Number(this.$route.query.activityId || 0);
      return raw > 0 ? raw : 0;
    },
    list() {
      return this.listData;
    },
  },
  watch: {
    "$route.query.activityId"() {
      this.params.page = 1;
      this.getList(this.params);
    },
  },
  mounted() {
    this.getList(this.params);
  },
  methods: {
    handleTabsActive(index) {
      this.tabsActive = index;
      localStorage.setItem("userCenterTabsActive", index);
      if (index === 0) {
        this.getList(this.params);
      }
    },
    onChange(page) {
      this.params.page = page;
      this.getList(this.params);
    },
    getList(params) {
      const requestParams = {
        ...params,
      };
      if (this.currentActivityId) {
        requestParams.activityId = this.currentActivityId;
      }
      userVoteWorks(requestParams).then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        this.listData = rows.map((item) => ({
          ...item,
          voteWorksVo: item.voteWorksVo
            ? {
                ...item.voteWorksVo,
                ...normalizeVoteMeta(item.voteWorksVo),
              }
            : null,
        }));
        this.total = Number(res.total || 0);
        this.totalPage = Number(res.totalPage || 0);
      });
    },
    handleView(id) {
      this.$router.push({
        name: "WorkCompetitionDetail",
        params: { id },
      });
    },
    handleEdit(work) {
      if (!work || !work.id || !work.activityId) {
        return;
      }
      this.$router.push({
        name: "ContestApply",
        params: { contestId: String(work.activityId) },
        query: {
          mode: "edit",
          workId: String(work.id),
          activityId: String(work.activityId),
        },
      });
    },
    canEdit(work) {
      return Boolean(work && work.canEdit);
    },
    canCancel(work) {
      return this.canEdit(work);
    },
    handleCancel(item) {
      const worksName = item?.voteWorksVo?.name || this.labels.defaultWork;
      this.$confirm({
        title: this.labels.cancelTitle,
        content: `${this.labels.cancelContentPrefix}${worksName}${this.labels.cancelContentSuffix}`,
        okText: this.labels.cancelOk,
        cancelText: this.labels.cancelBack,
        onOk: () =>
          userCancelVoteWorks({ id: item.id }).then(() => {
            this.$message.success(this.labels.cancelSuccess);
            this.getList(this.params);
          }),
      });
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : "--";
    },
    getCover(work) {
      return resolveWorkCover(work, FALLBACK_IMAGE);
    },
    handleImageError(event) {
      replaceBrokenImage(event, FALLBACK_IMAGE);
    },
    getAuthorName(work) {
      const extra = this.parseExtraJson(work.extraJson);
      return extra?.author?.name || work.realName || "--";
    },
    getCreatorTypeLabel(work) {
      const extra = this.parseExtraJson(work.extraJson);
      if (extra?.creatorType === "team" || work.creatorType === 1) {
        return this.labels.team;
      }
      if (extra?.creatorType === "personal" || work.creatorType === 0) {
        return this.labels.personal;
      }
      return "";
    },
    getParticipantProfile(work) {
      const extra = this.parseExtraJson(work.extraJson);
      const profile = extra?.participantProfile;
      return profile && typeof profile === "object" ? profile : {};
    },
    isProfessionalParticipant(work) {
      const profile = this.getParticipantProfile(work);
      if (profile.identity === "professional") {
        return true;
      }
      if (profile.identity === "student") {
        return false;
      }
      return String(work?.education || "").trim() === "非学生/在职人员";
    },
    getOrganizationTitle(work) {
      if (!this.isProfessionalParticipant(work)) {
        return work.schoolName || "--";
      }
      const profile = this.getParticipantProfile(work);
      return (
        String(profile.companyName || profile.company || "").trim() ||
        String(profile.identityLabel || "").trim() ||
        "非学生/在职人员"
      );
    },
    getCompanyName(work) {
      const profile = this.getParticipantProfile(work);
      return String(profile.companyName || profile.company || "").trim();
    },
    getIndustryField(work) {
      const profile = this.getParticipantProfile(work);
      return String(profile.industryField || work.speciality || "").trim();
    },
    getMentorText(work) {
      return [work.mentor, work.mentorSecond, work.mentorThird].filter(Boolean).join(" / ");
    },
    getWorkSizeText(work) {
      if (work.worksSize) {
        return work.worksSize;
      }
      const extra = this.parseExtraJson(work.extraJson);
      const currentWork = extra?.work || {};
      if (
        currentWork.length != null &&
        currentWork.width != null &&
        currentWork.height != null
      ) {
        const unit = currentWork.unit || "cm";
        return `${currentWork.length}${unit} x ${currentWork.width}${unit} x ${currentWork.height}${unit}`;
      }
      return "";
    },
    parseExtraJson(value) {
      if (!value) {
        return null;
      }
      try {
        return typeof value === "string" ? JSON.parse(value) : value;
      } catch (error) {
        return null;
      }
    },
  },
};
</script>

<style lang="scss">
.vote-works {
  .tabs {
    height: 47px;
    border-bottom: 1px solid #f2f2f2;
    display: flex;
    justify-content: left;

    span {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      font-size: 15px;
      color: #7f7f7f;
      line-height: 1;
      padding: 16px 0 13px;
      margin-right: 34px;
      min-width: 88px;
      user-select: none;
      cursor: pointer;

      .tabs-img {
        flex: 0 0 auto;
        width: 14px;
        height: 14px;
        margin-right: 10px;
      }
    }

    span.videoActive {
      color: #da514e;
      border-bottom: 2px solid #da514e;
    }
  }

  .vote-box-works {
    padding-top: 28px;
  }

  .works-box {
    margin-bottom: 20px;
  }

  .works-title {
    height: 42px;
    background: #f4edf4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
  }

  .works-info {
    display: flex;
    align-items: center;
    gap: 36px;
    color: #5b4f5f;
    font-size: 16px;
    font-weight: 500;
  }

  .works-btn {
    display: flex;
    align-items: center;
    gap: 28px;

    span {
      color: #393939;
      font-size: 16px;
      cursor: pointer;
      user-select: none;
    }

    span:hover {
      color: #da514e;
    }
  }

  .works-content {
    border: 1px solid #eee3ee;
    display: grid;
    grid-template-columns: minmax(0, 28%) minmax(0, 24%) minmax(0, 19%) minmax(0, 29%);
    min-height: 138px;
  }

  .con-primary,
  .con-school,
  .con-plate,
  .con-activity {
    min-width: 0;
    padding: 18px 18px;
    color: #666;
    font-size: 12px;
    line-height: 1.8;
  }

  .con-primary,
  .con-school,
  .con-plate {
    border-right: 1px solid #f0f0f0;
  }

  .con-primary {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .vote-con-img {
    width: 112px;
    height: 76px;
    object-fit: cover;
    border-radius: 2px;
    display: block;
    flex-shrink: 0;
  }

  .con-info {
    min-width: 0;
    color: #666;
    font-size: 12px;
    line-height: 1.95;
  }

  .con-info h5,
  .con-school h5,
  .con-plate h5 {
    margin: 0 0 8px;
    color: #333;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .con-info span,
  .con-school span,
  .con-plate span,
  .con-activity span,
  .con-school div,
  .con-activity div {
    display: block;
  }

  .meta-row {
    display: flex !important;
    align-items: baseline;
    gap: 6px;
    width: 100%;
    min-width: 0;
    white-space: nowrap;
  }

  .meta-label {
    flex: 0 0 auto;
  }

  .meta-value {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mentor-line {
    gap: 2px;
  }

  .mentor-text {
    white-space: nowrap;
  }

  .con-activity {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    text-align: center;
  }

  .activity-name {
    display: block !important;
    width: 100%;
    margin-bottom: 12px;
    color: #db514e;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.5;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .activity-meta {
    color: #666;
    display: flex !important;
    align-items: baseline;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
    line-height: 1.95;
    white-space: nowrap;
  }

  .empty-state {
    padding: 40px 0;
    color: #999;
    text-align: center;
  }

  .art-pagination {
    margin-top: 8px;
  }
}
</style>
